import { useCallback, useEffect, useId, useMemo, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import {
  beakShown, crawlDecision, inkRises, perchFor, pullEasing, watchdogState,
  type WatchdogBubble, type WatchdogBubbleEntry, type WatchdogInput, type WatchdogLink,
} from '../lib/watchdog';
import '../watchdog.css';

/**
 * The creature that lives on the top bar.
 *
 * Everything it decides lives in `lib/watchdog`; this file only draws, and
 * the drawing is built from an octopus rather than from a face: a soft mantle
 * with papillae on it, eyes set on the sides with horizontal slit pupils, a
 * funnel it breathes and inks through, eight arms that TAPER to a curl and
 * carry two staggered rows of suckers on the underside, and a small hard beak
 * at the centre of the arm crown - underneath, hidden until the arms part.
 *
 * It is decoration. Every state it shows is written next to it in words, and
 * the words are what a screen reader is given.
 */

type Pt = readonly [number, number];

/** A point along a Catmull-Rom spline through the arm's spine. */
function sample(spine: readonly Pt[], t: number): Pt {
  const last = spine.length - 1;
  const u = Math.min(0.999999, Math.max(0, t)) * last;
  const i = Math.floor(u);
  const f = u - i;
  const p0 = spine[Math.max(0, i - 1)] ?? spine[0]!;
  const p1 = spine[i] ?? spine[0]!;
  const p2 = spine[Math.min(last, i + 1)] ?? spine[last]!;
  const p3 = spine[Math.min(last, i + 2)] ?? spine[last]!;
  const axis = (a: number, b: number, c: number, d: number): number =>
    0.5 * (2 * b + (-a + c) * f + (2 * a - 5 * b + 4 * c - d) * f * f + (-a + 3 * b - 3 * c + d) * f * f * f);
  return [axis(p0[0], p1[0], p2[0], p3[0]), axis(p0[1], p1[1], p2[1], p3[1])];
}

const round = (n: number): string => (Math.round(n * 100) / 100).toString();

interface Rib { readonly x: number; readonly y: number; readonly nx: number; readonly ny: number; readonly w: number }

/**
 * An arm as a real arm: thick where it leaves the crown, thinning all the way
 * to a fine curled tip. Waving identical noodles is what made the first one
 * look drawn; a taper is what stops it.
 */
function armShape(spine: readonly Pt[], base: number, steps = 24): { d: string; ribs: readonly Rib[] } {
  const ribs: Rib[] = [];
  for (let s = 0; s <= steps; s += 1) {
    const t = s / steps;
    const here = sample(spine, t);
    const ahead = sample(spine, Math.min(1, t + 0.02));
    const behind = sample(spine, Math.max(0, t - 0.02));
    const dx = ahead[0] - behind[0];
    const dy = ahead[1] - behind[1];
    const len = Math.hypot(dx, dy) || 1;
    ribs.push({
      x: here[0], y: here[1], nx: -dy / len, ny: dx / len,
      w: base * Math.pow(1 - t, 0.8) + 0.45,
    });
  }
  const left = ribs.map((r) => `${round(r.x + r.nx * r.w)} ${round(r.y + r.ny * r.w)}`);
  const right = [...ribs].reverse().map((r) => `${round(r.x - r.nx * r.w)} ${round(r.y - r.ny * r.w)}`);
  return { d: `M${left.join(' L')} L${right.join(' L')} Z`, ribs };
}

/** Two staggered rows of suckers down the underside of one arm. */
function suckersOn(ribs: readonly Rib[]): readonly { x: number; y: number; r: number }[] {
  const out: { x: number; y: number; r: number }[] = [];
  for (let i = 4; i < ribs.length - 2; i += 2) {
    const rib = ribs[i]!;
    const row = i % 4 === 0 ? 0.62 : 0.24;
    out.push({
      x: rib.x - rib.nx * rib.w * row,
      y: rib.y - rib.ny * rib.w * row,
      r: Math.max(0.5, rib.w * 0.3),
    });
  }
  return out;
}

/** The eight arms, drawn once. Each is its own shape; none is a copy. */
const ARMS: readonly { key: string; spine: readonly Pt[]; base: number; phase: number; under: boolean }[] = [
  { key: 'reach', spine: [[80, 58], [96, 55], [112, 61], [124, 74], [119, 84]], base: 5.2, phase: 0, under: true },
  { key: 'lead', spine: [[80, 62], [94, 66], [106, 76], [112, 86], [105, 88]], base: 5.0, phase: 0, under: true },
  { key: 'grip', spine: [[78, 66], [86, 74], [92, 84], [97, 88]], base: 4.8, phase: 1, under: true },
  { key: 'bear', spine: [[76, 68], [78, 78], [80, 87]], base: 4.6, phase: 1, under: true },
  { key: 'trail', spine: [[73, 68], [68, 78], [62, 87]], base: 4.6, phase: 2, under: true },
  { key: 'drag', spine: [[70, 66], [58, 74], [46, 84], [40, 87]], base: 4.4, phase: 2, under: false },
  { key: 'curl', spine: [[68, 62], [52, 64], [38, 72], [30, 80], [33, 86]], base: 4.0, phase: 3, under: false },
  { key: 'lift', spine: [[70, 58], [54, 54], [40, 56], [32, 64], [36, 70]], base: 3.6, phase: 3, under: false },
];

/** A leaning egg, not a ball: the mantle is a soft bag slung back off the head. */
const MANTLE = 'M74 52C80 36 78 18 62 10C46 2 26 8 18 24C10 40 16 58 34 64C50 69 68 64 74 52Z';
/** Papillae: the skin bumps an octopus raises. Small, or it reads as spots. */
const PAPILLAE: readonly Pt[] = [[36, 16], [50, 14], [28, 27], [24, 39], [42, 25], [56, 24], [34, 35], [47, 37]];

function Creature({ uid }: { uid: string }): ReactElement {
  const arms = useMemo(() => ARMS.map((arm) => ({ ...arm, ...armShape(arm.spine, arm.base) })), []);
  const clipId = `${uid}-skin`;
  const flushId = `${uid}-flush`;
  return (
    <svg className="wd-svg" viewBox="0 0 132 96" aria-hidden="true" focusable="false">
      <defs>
        <radialGradient id={flushId} cx="0.32" cy="0.3" r="0.72">
          <stop className="wd-flush-in" offset="0" />
          <stop className="wd-flush-mid" offset="0.55" />
          <stop className="wd-flush-out" offset="1" />
        </radialGradient>
        <clipPath id={clipId}>
          <path d={MANTLE} />
          {arms.map((arm) => <path key={arm.key} d={arm.d} />)}
        </clipPath>
      </defs>

      {/* Ink leaves through the funnel, which is where it really leaves. */}
      <g className="wd-ink">
        <ellipse cx="90" cy="62" rx="9" ry="7" />
        <circle cx="103" cy="66" r="4.4" />
        <circle cx="112" cy="71" r="2.6" />
        <circle cx="97" cy="72" r="3" />
      </g>

      <g className="wd-back">
        {arms.filter((arm) => !arm.under).map((arm) => (
          <g key={arm.key} className={`wd-arm wd-p${arm.phase} wd-arm-${arm.key}`}><path d={arm.d} /></g>
        ))}
      </g>

      <g className="wd-body">
        <path className="wd-mantle" d={MANTLE} />
        {PAPILLAE.map(([x, y]) => <ellipse key={`${x}-${y}`} className="wd-papilla" cx={x} cy={y} rx="2.7" ry="1.9" />)}
        <path className="wd-shade" d="M18 24C12 42 18 58 34 64C50 69 63 66 71 57C59 67 38 67 26 57C17 49 14 36 18 24Z" />
        <path className="wd-funnel" d="M66 56C71 54 77 56 79 60C75 62 68 61 65 59Z" />
        {/* The far eye: an octopus's eyes sit on the SIDES of the head, so
            only one is square on and the other is barely a bulge past the
            mantle's edge. Two eyes level and forward is a face, not an
            octopus. */}
        <ellipse className="wd-eye wd-eye-far" cx="57" cy="34" rx="5.2" ry="4.4" />
        <rect className="wd-slit wd-slit-far" x="52.6" y="32.7" width="8.8" height="2.6" rx="1.3" />
        <ellipse className="wd-eye" cx="70" cy="48" rx="9.6" ry="8.2" />
        <rect className="wd-slit" x="63.2" y="45.9" width="13.6" height="4.2" rx="2.1" />
        <path className="wd-lid" d="M60 44C64 38 76 37 80 43" />
        <path className="wd-crease" d="M64 61C69 58 72 55 73 50" />
        <circle className="wd-glint" cx="65.6" cy="44.6" r="1.6" />
      </g>

      <g className="wd-front">
        {arms.filter((arm) => arm.under).map((arm) => (
          <g key={arm.key} className={`wd-arm wd-p${arm.phase} wd-arm-${arm.key}`}>
            <path d={arm.d} />
            {suckersOn(arm.ribs).map((s, i) => (
              <circle key={i} className="wd-sucker" cx={round(s.x)} cy={round(s.y)} r={round(s.r)} />
            ))}
          </g>
        ))}
      </g>

      {/* The beak: at the centre of the arm crown, on the UNDERSIDE. It is
          drawn over the arms because you only ever see it in the gap they
          leave when they part - and it is only shown when they have. */}
      <g className="wd-mouth">
        <ellipse className="wd-web" cx="78" cy="70" rx="8.4" ry="5.6" />
        <path className="wd-beak wd-beak-top" d="M72.6 68.2C75.6 66.6 80.2 66.9 82.8 69.4C79.6 68.6 75.6 68.8 72.6 70Z" />
        <path className="wd-beak wd-beak-low" d="M73.6 70.8C76.6 72 80 71.8 82.4 70.6C80.8 74.6 75.6 74.8 73.6 70.8Z" />
      </g>

      {/* Chromatophores: the new colour spreads out from the mantle through
          the skin rather than the whole drawing repainting at once. */}
      <g className="wd-chroma" clipPath={`url(#${clipId})`}>
        <ellipse cx="44" cy="36" rx="58" ry="46" fill={`url(#${flushId})`} />
      </g>
    </svg>
  );
}

function prefersStill(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

interface Walk {
  readonly x: number;
  readonly ms: number;
  readonly ease: string;
  readonly dir: 'left' | 'right';
  readonly cycle: number;
}

/**
 * The creature, mounted on the top bar.
 *
 * It holds still until the reading changes, then hauls itself - one arm cycle
 * per pull - to where that reading belongs, and holds still again. Between
 * moves its look carries the mood - idle, working, needs you, failed - with
 * slow, small motion that stops when motion is turned down.
 *
 * Given a bubble, the creature is a button: clicking it shows what is
 * running, what needs the owner and the last finished run, each opening the
 * view it names through `onOpen`.
 */
export function Watchdog({ onOpenFailures, bubble, onOpen, ...props }: WatchdogInput & {
  /** Opens the list of the same current failures the headline counts. */
  readonly onOpenFailures?: () => void;
  /** What the bubble lists, built from rows the room already holds. */
  readonly bubble?: WatchdogBubble;
  /** Opens the existing view or record an entry names. */
  readonly onOpen?: (link: WatchdogLink) => void;
}): ReactElement {
  const uid = useId().replace(/:/g, '');
  const reading = watchdogState(props);
  const target = perchFor(reading.tone);

  const still = useRef(prefersStill());
  const at = useRef(target);
  const [walk, setWalk] = useState<Walk>(() => ({ x: target, ms: 0, ease: 'linear(0, 1)', dir: 'right', cycle: 0 }));
  const [crawling, setCrawling] = useState(false);
  const [inking, setInking] = useState(false);
  const [flushing, setFlushing] = useState(false);
  const seen = useRef<number | null>(null);
  const tone = useRef(reading.tone);

  useEffect(() => {
    const decision = crawlDecision({ at: at.current, to: target, reducedMotion: still.current });
    if (!decision.move) return;
    at.current = target;
    setWalk((current) => ({
      x: target,
      ms: decision.durationMs,
      ease: pullEasing(decision.strides),
      dir: decision.direction === 'left' ? 'left' : 'right',
      cycle: (current.cycle + 1) % 2,
    }));
    if (decision.strides === 0) return;
    setCrawling(true);
    const timer = window.setTimeout(() => setCrawling(false), decision.durationMs + 260);
    return () => window.clearTimeout(timer);
  }, [target]);

  useEffect(() => {
    const before = seen.current;
    seen.current = reading.attention;
    if (!inkRises(before, reading.attention) || still.current) return;
    setInking(true);
    const timer = window.setTimeout(() => setInking(false), 1600);
    return () => window.clearTimeout(timer);
  }, [reading.attention]);

  useEffect(() => {
    if (tone.current === reading.tone) return;
    tone.current = reading.tone;
    setFlushing(true);
    const timer = window.setTimeout(() => setFlushing(false), 1100);
    return () => window.clearTimeout(timer);
  }, [reading.tone]);

  const classes = ['wd-walker', `wd-cycle${walk.cycle}`];
  if (crawling) classes.push('wd-crawling');
  if (inking) classes.push('wd-inking');
  if (flushing) classes.push('wd-flushing');
  if (beakShown({ crawling, tone: reading.tone })) classes.push('wd-beak-out');

  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const bubbleId = `${uid}-bubble`;
  const clickable = bubble !== undefined && onOpen !== undefined;

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) button.current?.focus();
  }, []);
  const toggle = useCallback(() => {
    if (open) { close(false); return; }
    // Placed once, under the creature and kept on screen, when it opens.
    const rect = button.current?.getBoundingClientRect();
    if (rect !== undefined) {
      const width = Math.min(BUBBLE_WIDTH, window.innerWidth - 16);
      setPlace({
        left: Math.round(Math.min(Math.max(8, rect.left + rect.width / 2 - width / 2), window.innerWidth - width - 8)),
        top: Math.round(rect.bottom + 8),
      });
    }
    setOpen(true);
  }, [open, close]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => { if (event.key === 'Escape') close(true); };
    const onPointer = (event: PointerEvent): void => {
      const target = event.target as Node | null;
      if (target !== null && (panel.current?.contains(target) || button.current?.contains(target))) return;
      close(false);
    };
    // A bubble placed for one layout is wrong for the next one: close it.
    const onResize = (): void => close(false);
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close]);

  const choose = useCallback((link: WatchdogLink) => {
    setOpen(false);
    onOpen?.(link);
  }, [onOpen]);

  const walkerStyle = { '--wd-x': `${walk.x}`, '--wd-ms': `${walk.ms}ms`, '--wd-ease': walk.ease } as CSSProperties;

  return (
    <div className="watchdog" data-tone={reading.tone} data-mood={reading.mood}>
      {reading.subject === 'failed' && onOpenFailures !== undefined
        ? <button type="button" className="wd-word" title={`${reading.detail} · Open the failures`} onClick={onOpenFailures}>{reading.headline}</button>
        : <span className="wd-word" title={reading.detail}>{reading.headline}</span>}
      <div className="wd-ledge">
        {clickable
          ? (
            <button
              ref={button}
              type="button"
              className={`${classes.join(' ')} wd-clickable`}
              data-dir={walk.dir}
              style={walkerStyle}
              aria-label={`${reading.headline}. Show what is running and what needs you`}
              title="What is running and what needs you"
              aria-expanded={open}
              aria-controls={bubbleId}
              onClick={toggle}
            >
              <Creature uid={uid} />
            </button>
          )
          : (
            <div className={classes.join(' ')} data-dir={walk.dir} style={walkerStyle}>
              <Creature uid={uid} />
            </div>
          )}
      </div>
      {clickable && open && (
        <div
          ref={panel}
          id={bubbleId}
          className="wd-bubble"
          role="dialog"
          aria-label="What Brunel is doing"
          style={place === null ? undefined : { left: `${place.left}px`, top: `${place.top}px` }}
        >
          <BubbleBody bubble={bubble} headline={reading.headline} onChoose={choose} />
        </div>
      )}
    </div>
  );
}

/** How wide the bubble is, at most; narrower screens get the screen less a margin. */
const BUBBLE_WIDTH = 320;

function BubbleEntry({ entry, onChoose }: { entry: WatchdogBubbleEntry; onChoose: (link: WatchdogLink) => void }): ReactElement {
  return (
    <li>
      <button type="button" className="wd-bubble-entry" data-link={entry.link.kind} onClick={() => onChoose(entry.link)}>
        <span className="wd-bubble-label">{entry.label}</span>
        <small className="wd-bubble-detail">{entry.detail}</small>
      </button>
    </li>
  );
}

/** The bubble's three sections. Each says plainly when it has nothing to show. */
export function BubbleBody({ bubble, headline, onChoose }: {
  bubble: WatchdogBubble;
  headline: string;
  onChoose: (link: WatchdogLink) => void;
}): ReactElement {
  return (
    <>
      <p className="wd-bubble-head">{headline}</p>
      <section className="wd-bubble-section" data-section="running">
        <h3>Running</h3>
        {bubble.running.length === 0
          ? <p className="wd-bubble-empty">Nothing running.</p>
          : (
            <ul>
              {bubble.running.map((entry) => <BubbleEntry key={entry.key} entry={entry} onChoose={onChoose} />)}
              {bubble.moreRunning !== null && <BubbleEntry entry={bubble.moreRunning} onChoose={onChoose} />}
            </ul>
          )}
      </section>
      <section className="wd-bubble-section" data-section="needs-you">
        <h3>Needs you</h3>
        {bubble.needsYou.length === 0
          ? <p className="wd-bubble-empty">Nothing needs you.</p>
          : <ul>{bubble.needsYou.map((entry) => <BubbleEntry key={entry.key} entry={entry} onChoose={onChoose} />)}</ul>}
      </section>
      <section className="wd-bubble-section" data-section="last-finished">
        <h3>Last finished</h3>
        {bubble.lastFinished === null
          ? <p className="wd-bubble-empty">No finished run yet.</p>
          : <ul><BubbleEntry entry={bubble.lastFinished} onChoose={onChoose} /></ul>}
      </section>
    </>
  );
}
